import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';
import type { TypeGetWorkApplicationsPreviousResponse } from '~/api/workApplications';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';
import { type TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

export const WORK_APPLY_PREVIOUS_CONDITION_QUERY_KEY = ['work-applications', 'previous'] as const;

/** @description 최근 근무지원 정보 조회 */
export const useGetWorkApplyPreviousCondition = () => {
  const queryClient = useQueryClient();

  return async ({
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TypeGetWorkApplicationsPreviousResponse) => void;
    onError?: (err: TypeErrorFormat) => void;
  } = {}) => {
    const query = queryClient.ensureQueryData<AxiosResponse<TypeGetWorkApplicationsPreviousResponse>>({
      queryKey: WORK_APPLY_PREVIOUS_CONDITION_QUERY_KEY,
      queryFn: () => axiosInstance.get<TypeGetWorkApplicationsPreviousResponse>('/work-applications/previous'),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError);
  };
};

registerMockAPI<TypeGetWorkApplicationsPreviousResponse>({
  method: 'get',
  endpoint: '/work-applications/previous',
  label: '근무 지원 정보/이전 근무 조건 조회',
  scenarios: [
    {
      name: '이전 조건 있음 (1/2지망)',
      response: {
        code: '200',
        message: 'OK',
        data: {
          tid: 'mock-tid-001',
          workLocationId: 1,
          workLocationName: '김포',
          task: {
            code: 'RECEIVING',
            text: '입고',
            description: '검수가 완료된 상품을 진열대에 배치⋅보충해요.',
          },
          commutingMethod: { code: 'CAR', text: '자차' },
          applications: [
            {
              workShift: '13:00 ~ 22:00',
              priority: { code: 'FIRST', text: '1지망' },
              workEnvironment: { code: 'NORMAL', text: '상온' },
            },
            {
              workShift: '16:00 ~ 00:50',
              priority: { code: 'SECOND', text: '2지망' },
              workEnvironment: { code: 'BEAUTY', text: '뷰티전처리센터' },
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsPreviousResponse,
    },
    {
      name: '이전 조건 있음 (1지망)',
      response: {
        code: '200',
        message: 'OK',
        data: {
          tid: 'mock-tid-001',
          workLocationId: 1,
          workLocationName: '김포',
          task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
          commutingMethod: { code: 'CAR', text: '자차' },
          applications: [
            {
              workShift: '13:00 ~ 22:00',
              priority: { code: 'FIRST', text: '1지망' },
              workEnvironment: { code: 'NORMAL', text: '상온' },
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsPreviousResponse,
    },
    {
      name: '이전 조건 없음',
      response: {
        code: '200',
        message: 'OK',
        data: {},
      } as unknown as TypeGetWorkApplicationsPreviousResponse,
    },
  ],
});
